import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Printer, ChevronLeft, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import VoiceButton from "@/components/VoiceButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";

const OUTCOME_LABELS = {
  returned_to_event: "Returned to event",
  left_scene: "Left scene (own accord)",
  taken_to_hospital: "Taken to hospital",
  refused_care: "Refused care / self-discharged",
  other: "Other",
};

function fmt(dt) {
  if (!dt) return "—";
  const d = new Date(dt);
  return `${d.toLocaleDateString("en-GB")} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

function treatmentSummary(p) {
  const bits = [];
  if (p.observations.length > 0) {
    const last = p.observations[p.observations.length - 1];
    bits.push(`${p.observations.length} observation set${p.observations.length > 1 ? "s" : ""} (final NEWS2 ${last.news2})`);
  }
  if (p.medications.length > 0) {
    bits.push("Medications: " + p.medications.map((m) => `${m.medication}${m.dosage ? ` ${m.dosage}` : ""}`).join(", "));
  }
  const comments = p.observations.map((o) => o.comment).filter(Boolean);
  if (comments.length > 0) bits.push(comments[comments.length - 1]);
  const noteTexts = (p.notes || []).filter(Boolean);
  if (noteTexts.length > 0) bits.push("Notes: " + noteTexts.join(" / "));
  return bits.join(" · ") || "Assessed on scene";
}

function todayLondon() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(new Date());
}

export default function OrganizerReport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState(searchParams.get("event") || "all");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customTitle, setCustomTitle] = useState("End of Event Medical Report");
  const [customEvent, setCustomEvent] = useState("");
  const [customVenue, setCustomVenue] = useState("");
  const [organiserNotes, setOrganiserNotes] = useState("");
  const [reportDate, setReportDate] = useState(() => searchParams.get("date") || todayLondon());
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [eventContacts, setEventContacts] = useState({ client_name: "", default_email: "", contacts: [] });
  const [contactChoice, setContactChoice] = useState("__custom");
  const [debrief, setDebrief] = useState({ went_well: "", improve: "", incidents: "" });
  const [debriefSaving, setDebriefSaving] = useState(false);
  const [qconfig, setQconfig] = useState({ went_well: true, improve: true, incidents: true, overall_note: true });
  const [customising, setCustomising] = useState(false);

  const toggleQuestion = async (key) => {
    const next = { ...qconfig, [key]: !qconfig[key] };
    setQconfig(next);
    try {
      await api.put("/reports/question-config", { questions: next });
    } catch (err) {
      setQconfig(qconfig);
      toast.error(formatApiError(err));
    }
  };

  const saveDebrief = async () => {
    setDebriefSaving(true);
    try {
      await api.put("/reports/debrief", {
        event, date: reportDate,
        went_well: qconfig.went_well ? debrief.went_well : "",
        improve: qconfig.improve ? debrief.improve : "",
        incidents: qconfig.incidents ? debrief.incidents : "",
        overall_note: qconfig.overall_note ? organiserNotes : "",
      });
      toast.success("Debrief & overall note saved — they're included in the email, print and WhatsApp versions");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDebriefSaving(false);
    }
  };

  const shareWhatsApp = async () => {
    if (!report) return;
    let link = "";
    try {
      const { data } = await api.post("/reports/event-summary/share", {
        event, date: reportDate, title: customTitle, venue: customVenue, notes: organiserNotes,
      });
      link = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      return;
    }
    const lines = [
      `*${customTitle}*`,
      `Event: ${customEvent}${customVenue ? ` · Venue: ${customVenue}` : ""}${reportDate ? ` · Date: ${reportDate}` : ""}`,
      "",
      `Treated: ${report.counts.treated} · To hospital: ${report.counts.hospital} · Refused care: ${report.counts.refused} · Lost children: ${report.lost_children?.total ?? 0}`,
    ];
    if (qconfig.went_well && debrief.went_well.trim()) lines.push("", `*What went well:* ${debrief.went_well}`);
    if (qconfig.improve && debrief.improve.trim()) lines.push("", `*Better next time:* ${debrief.improve}`);
    if (qconfig.incidents && debrief.incidents.trim()) lines.push("", `*Incidents of note:* ${debrief.incidents}`);
    if (qconfig.overall_note && organiserNotes.trim()) lines.push("", `*Notes:* ${organiserNotes}`);
    if (link) lines.push("", `Full report (PDF): ${link}`);
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  const sendReportEmail = async (e) => {
    e.preventDefault();
    setEmailSending(true);
    try {
      const { data } = await api.post("/reports/event-summary/email", {
        to_email: emailTo,
        event,
        title: customTitle,
        venue: customVenue,
        notes: organiserNotes,
        date: reportDate,
      });
      toast.success(data.message);
      setEmailOpen(false);
      setEmailTo("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmailSending(false);
    }
  };

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setEventContacts({ client_name: "", default_email: "", contacts: [] });
    setContactChoice("__custom");
    if (event === "all" || event === "__none__") return;
    api.get("/reports/event-contacts", { params: { event } })
      .then((r) => {
        setEventContacts(r.data);
        if (r.data.default_email) {
          setEmailTo(r.data.default_email);
          setContactChoice(r.data.default_email);
        }
      })
      .catch(() => {});
  }, [event]);

  useEffect(() => {
    document.title = `${customTitle} - ${customEvent || "WEMS"}`;
    return () => { document.title = "Wales Event Medical — Staff Portal"; };
  }, [customTitle, customEvent]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/reports/event-summary", { params: { event, ...(reportDate ? { date: reportDate } : {}) } })
      .then((r) => {
        setReport(r.data);
        setCustomEvent(event === "all" ? "All Events" : event === "__none__" ? "Unassigned Patients" : event);
        setCustomVenue([...new Set(r.data.patients.map((p) => p.venue).filter(Boolean))].join(", "));
      })
      .catch((e) => {
        toast.error(formatApiError(e));
        navigate("/dashboard");
      })
      .finally(() => setLoading(false));
    api.get("/reports/debrief", { params: { event, date: reportDate } })
      .then((r) => {
        setDebrief({ went_well: r.data.went_well || "", improve: r.data.improve || "", incidents: r.data.incidents || "" });
        setOrganiserNotes(r.data.overall_note || "");
      })
      .catch(() => {});
    api.get("/reports/question-config")
      .then((r) => setQconfig(r.data))
      .catch(() => {});
  }, [event, reportDate, navigate]);

  const injuryCounts = useMemo(() => {
    const counts = {};
    if (report) {
      report.patients.forEach((p) =>
        (p.injuries || []).forEach((i) => {
          counts[i] = (counts[i] || 0) + 1;
        })
      );
    }
    return counts;
  }, [report]);
  const sortedInjuries = useMemo(
    () => Object.entries(injuryCounts).sort((a, b) => b[1] - a[1]),
    [injuryCounts]
  );

  const dateEvents = useMemo(
    () => (reportDate ? events.filter((e) => e.event_date === reportDate) : events),
    [events, reportDate]
  );
  useEffect(() => {
    if (event !== "all" && event !== "__none__" && !dateEvents.some((e) => e.name === event)) setEvent("all");
  }, [dateEvents, event]);

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white">
      <div className="no-print sticky top-0 z-40 bg-[#0F2547] text-white px-4 sm:px-8 py-3 flex justify-between items-center gap-3">
        <button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn"
          className="inline-flex items-center gap-1 text-sm font-semibold hover:text-sky-300 transition-colors min-h-[44px]">
          <ChevronLeft className="h-4 w-4" /> Dashboard
        </button>
        <div className="flex items-center gap-2">
          <input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)}
            data-testid="report-date-filter" title="Pick a date first — the event list then shows only that date's events"
            className="h-11 rounded-lg border border-slate-600 bg-[#173766] text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
          <select value={event} onChange={(e) => setEvent(e.target.value)} data-testid="organizer-event-select"
            className="h-11 rounded-lg border border-slate-600 bg-[#173766] text-white px-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-sky-400 max-w-[180px] sm:max-w-none">
            <option value="all">{reportDate ? "All events on this date" : "All events"}</option>
            {dateEvents.map((e) => <option key={e.name} value={e.name}>{e.name}</option>)}
            <option value="__none__">Unassigned patients</option>
          </select>
          {reportDate !== todayLondon() && (
            <button onClick={() => setReportDate(todayLondon())} data-testid="report-today-btn"
              className="h-11 px-3 rounded-lg border border-slate-600 text-xs font-bold text-slate-300 hover:text-white hover:border-sky-400 transition-colors">
              Today
            </button>
          )}
          {reportDate && (
            <button onClick={() => setReportDate("")} data-testid="report-all-dates-btn"
              className="h-11 px-3 rounded-lg border border-slate-600 text-xs font-bold text-slate-300 hover:text-white hover:border-sky-400 transition-colors">
              All dates
            </button>
          )}
          <button onClick={shareWhatsApp} data-testid="whatsapp-report-button"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb857] transition-colors text-sm font-semibold px-4 py-2.5 rounded-lg min-h-[44px]">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </button>
          <button onClick={() => setEmailOpen(true)} data-testid="email-report-button"
            className="inline-flex items-center gap-2 bg-[#007F3B] hover:bg-[#006631] transition-colors text-sm font-semibold px-4 py-2.5 rounded-lg min-h-[44px]">
            <Mail className="h-4 w-4" /> Email
          </button>
          <button onClick={() => window.print()} data-testid="print-organizer-report-button"
            className="inline-flex items-center gap-2 bg-[#005EB8] hover:bg-[#004a93] transition-colors text-sm font-semibold px-4 py-2.5 rounded-lg min-h-[44px]">
            <Printer className="h-4 w-4" /> Print / PDF
          </button>
        </div>
      </div>

      <div className="print-page max-w-5xl mx-auto bg-white my-6 print:my-0 p-8 sm:p-12 shadow-lg print:shadow-none" data-testid="organizer-report">
        {loading || !report ? (
          <p className="text-sm text-slate-400 py-20 text-center">Building report…</p>
        ) : (
          <>
            <p className="no-print text-xs text-[#005EB8] font-semibold mb-4 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2" data-testid="report-edit-hint">
              Tip: the report title, event name, venue and notes below are editable — make your changes, then use Print / PDF to download.
            </p>
            <div className="flex items-start justify-between border-b-4 border-[#005EB8] pb-6 mb-6">
              <div className="flex-1 mr-6">
                <img src={LOGO} alt="Wales Event Medical Services" className="h-14 w-auto mb-3" />
                <input value={customTitle} onChange={(e) => setCustomTitle(e.target.value)} data-testid="report-title-input"
                  className="font-heading text-2xl font-extrabold text-[#0F2547] w-full bg-transparent border-b border-dashed border-slate-300 focus:border-[#005EB8] focus:outline-none print:border-transparent" />
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="font-semibold shrink-0 w-12">Event:</span>
                    <input value={customEvent} onChange={(e) => setCustomEvent(e.target.value)} data-testid="report-event-input"
                      className="flex-1 bg-transparent border-b border-dashed border-slate-300 focus:border-[#005EB8] focus:outline-none print:border-transparent text-xs text-slate-700 font-semibold" />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="font-semibold shrink-0 w-12">Venue:</span>
                    <input value={customVenue} onChange={(e) => setCustomVenue(e.target.value)} data-testid="report-venue-input"
                      placeholder="Venue name" className="flex-1 bg-transparent border-b border-dashed border-slate-300 focus:border-[#005EB8] focus:outline-none print:border-transparent text-xs text-slate-700" />
                  </div>
                  <p className="text-xs text-slate-500">
                    Prepared for the event organiser · Generated {fmt(report.generated_at)} by {report.generated_by}
                    {report.date
                      ? ` · Date: ${new Date(`${report.date}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}`
                      : " · All dates"}
                  </p>
                </div>
              </div>
              <div className="text-right text-xs text-slate-500">
                <p className="font-bold text-[#0F2547]">Wales Event Medical Services</p>
                <p>info@waleseventmedical.co.uk</p>
              </div>
            </div>

            <section className="mb-8" data-testid="organizer-summary">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-3">Summary</h2>
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 text-center">
                {[
                  ["Treated", report.counts.treated, "text-[#0F2547]"],
                  ["Active", report.counts.active, "text-[#005EB8]"],
                  ["Priority", report.counts.high, "text-[#D5281B]"],
                  ["Discharged", report.counts.discharged, "text-slate-600"],
                  ["To hospital", report.counts.hospital, "text-[#ED8B00]"],
                  ["Refused care", report.counts.refused, "text-slate-600"],
                  ["Lost children", report.lost_children?.total ?? 0, "text-[#007F3B]"],
                ].map(([label, val, cls]) => (
                  <div key={label} className="border border-slate-200 rounded-lg py-3">
                    <p className={`font-vitals text-2xl font-extrabold ${cls}`} data-testid={`organizer-count-${label.toLowerCase().replace(/ /g, "-")}`}>{val}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mt-1">{label}</p>
                  </div>
                ))}
              </div>
            </section>

            {report.counts.treated === 0 && (
              <div className="border-2 border-emerald-200 bg-emerald-50 rounded-xl px-4 py-3 mb-8" data-testid="no-patients-statement">
                <p className="font-heading font-bold text-[#007F3B] text-sm">
                  No patients were treated{report.date ? " on this date" : " at this event"} — medical cover was in place throughout.
                </p>
              </div>
            )}

            <section className="mb-8">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-3">Age range of patients</h2>
              <div className="flex flex-wrap gap-2">
                {Object.entries(report.age_bands).map(([band, n]) => (
                  <span key={band} className="border border-slate-200 rounded-full px-3 py-1.5 text-xs font-semibold text-slate-700">
                    {band}: <span className="font-vitals font-bold text-[#0F2547]">{n}</span>
                  </span>
                ))}
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-3">Injury breakdown</h2>
              {sortedInjuries.length === 0 ? (
                <p className="text-sm text-slate-500">No injury sites recorded.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {sortedInjuries.map(([site, n]) => (
                    <span key={site} className="bg-red-50 border border-red-200 text-[#D5281B] rounded-full px-3 py-1.5 text-xs font-bold">
                      {site} × {n}
                    </span>
                  ))}
                </div>
              )}
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-1">
                Casualty log ({report.patients.length})
              </h2>
              <p className="text-[11px] text-slate-400 mb-3">Patient names are withheld from this report for confidentiality.</p>
              {report.patients.length === 0 ? (
                <p className="text-sm text-slate-500">No patients recorded for this selection.</p>
              ) : (
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#0F2547] text-white">
                      <th className="text-left px-2 py-2">Time seen</th>
                      <th className="text-left px-2 py-2">Casualty</th>
                      <th className="px-2 py-2">Age</th>
                      <th className="text-left px-2 py-2">Injury site(s)</th>
                      <th className="text-left px-2 py-2">Treatment summary</th>
                      <th className="text-left px-2 py-2">Disposition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.patients.map((p, i) => (
                      <tr key={p.id} className={i % 2 ? "bg-slate-50" : ""} data-testid={`organizer-patient-row-${p.id}`}>
                        <td className="px-2 py-2 whitespace-nowrap font-semibold">{fmt(p.arrived_at || p.created_at)}</td>
                        <td className="px-2 py-2 font-semibold">Casualty {i + 1}</td>
                        <td className="px-2 py-2 text-center">{p.age ?? "—"}</td>
                        <td className="px-2 py-2">{p.injuries?.length ? p.injuries.join(", ") : "—"}</td>
                        <td className="px-2 py-2 max-w-[260px]">{treatmentSummary(p)}</td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          {p.status === "discharged" ? OUTCOME_LABELS[p.discharge_outcome] || "Discharged" : "Under care"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {qconfig.overall_note && (
            <section className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8]">Overall note</h2>
                <span className="no-print">
                  <VoiceButton testid="report-notes-voice"
                    onTranscript={(t) => setOrganiserNotes((n) => (n ? n + " " : "") + t)} />
                </span>
              </div>
              <textarea value={organiserNotes} onChange={(e) => setOrganiserNotes(e.target.value)} rows={3}
                data-testid="report-notes-input" placeholder="Your overall note for the organiser — type or tap the mic and talk… (saved with the debrief button below)"
                className="w-full rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] print:border-0 print:px-0" />
            </section>
            )}

            <section className="mb-8" data-testid="report-debrief-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8]">Event debrief</h2>
                <div className="flex items-center gap-2 no-print">
                  <button onClick={() => setCustomising((c) => !c)} data-testid="report-customise-btn"
                    className={`h-10 px-4 rounded-lg border text-xs font-bold transition-colors ${customising ? "bg-[#005EB8] border-[#005EB8] text-white" : "border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8]"}`}>
                    Customise questions
                  </button>
                  <button onClick={saveDebrief} disabled={debriefSaving} data-testid="save-debrief-btn"
                    className="h-10 px-4 rounded-lg bg-[#0F2547] text-white text-xs font-bold disabled:opacity-60">
                    {debriefSaving ? "Saving…" : "Save debrief"}
                  </button>
                </div>
              </div>
              {customising && (
                <div className="no-print mb-4 rounded-xl border border-sky-200 bg-sky-50 p-3" data-testid="report-question-toggles">
                  <p className="text-[11px] font-bold text-[#005EB8] uppercase tracking-wider mb-2">Turn questions on or off — off questions are hidden everywhere (report, print, email, WhatsApp)</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ["went_well", "What went well"],
                      ["improve", "Better next time"],
                      ["incidents", "Incidents of note"],
                      ["overall_note", "Overall note"],
                    ].map(([key, label]) => (
                      <button key={key} onClick={() => toggleQuestion(key)} data-testid={`report-toggle-${key}`} aria-pressed={qconfig[key]}
                        className={`h-9 px-3 rounded-full border text-xs font-bold transition-colors ${qconfig[key] ? "bg-[#007F3B] border-[#007F3B] text-white" : "bg-white border-slate-300 text-slate-400 line-through"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {[
                ["went_well", "What went well", "report-debrief-well"],
                ["improve", "What would make it better next time", "report-debrief-improve"],
                ["incidents", "Incidents of note", "report-debrief-incidents"],
              ].filter(([key]) => qconfig[key]).map(([key, label, testid]) => (
                <div key={key} className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-slate-600">{label}</label>
                    <span className="no-print">
                      <VoiceButton testid={`${testid}-voice`}
                        onTranscript={(t) => setDebrief((d) => ({ ...d, [key]: (d[key] ? d[key] + " " : "") + t }))} />
                    </span>
                  </div>
                  <textarea value={debrief[key]} rows={2} data-testid={testid}
                    onChange={(e) => setDebrief((d) => ({ ...d, [key]: e.target.value }))}
                    placeholder="Type or tap the mic and talk…"
                    className="w-full rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] print:border-0 print:px-0" />
                </div>
              ))}
            </section>

            <section className="mb-8" data-testid="organizer-lost-children">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-3">
                Lost children ({report.lost_children?.total ?? 0})
              </h2>
              {(report.lost_children?.total ?? 0) === 0 ? (
                <p className="text-sm text-slate-500">No lost children reported{report.date ? " on this date" : " during the event"}.</p>
              ) : (
                <>
                  <p className="text-xs font-semibold text-slate-600 mb-3" data-testid="organizer-lost-summary">
                    {report.lost_children.with_us} still with us · {report.lost_children.reunited} reunited · {report.lost_children.total} reported throughout the event
                  </p>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-[#0F2547] text-white">
                        <th className="text-left px-2 py-2">Time reported</th>
                        <th className="text-left px-2 py-2">Ref</th>
                        <th className="text-left px-2 py-2">Description</th>
                        <th className="text-left px-2 py-2">Details</th>
                        <th className="text-left px-2 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.lost_children.entries.map((c, i) => (
                        <tr key={c.id} className={i % 2 ? "bg-slate-50" : ""} data-testid={`organizer-lost-row-${c.id}`}>
                          <td className="px-2 py-2 whitespace-nowrap font-semibold">{fmt(c.created_at)}</td>
                          <td className="px-2 py-2 font-semibold">Child {i + 1}</td>
                          <td className="px-2 py-2">{c.clothing || "—"}</td>
                          <td className="px-2 py-2 max-w-[220px]">
                            {[{ all_ok: "Happy / no concern", concern: "Concern noted", referred: "Safeguarding referral made" }[c.safeguarding_status], c.reunite_checks].filter(Boolean).join("; ") || "—"}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            {c.status === "reunited" ? `Reunited${c.reunited_at ? ` ${fmt(c.reunited_at)}` : ""}` : "With us"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </section>

            <footer className="border-t border-slate-200 pt-4 text-[11px] text-slate-400 flex justify-between">
              <span>Wales Event Medical Services · Professional Event Medical Cover UK</span>
              <span>Confidential — for event organiser records</span>
            </footer>
          </>
        )}
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Email this report</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              The report is sent exactly as shown (anonymised, with your edits) to the address below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendReportEmail}>
            {eventContacts.contacts.length > 0 && (
              <div className="mb-3">
                <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                  Send to someone at {eventContacts.client_name}
                </label>
                <select value={contactChoice} data-testid="email-report-contact-select"
                  onChange={(e) => {
                    const v = e.target.value;
                    setContactChoice(v);
                    if (v !== "__custom") setEmailTo(v);
                  }}
                  className="w-full h-12 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]">
                  {eventContacts.contacts.map((c) => (
                    <option key={c.email} value={c.email}>
                      {c.name}{c.role ? ` — ${c.role}` : ""}{c.is_main ? " (main contact)" : ""}
                    </option>
                  ))}
                  <option value="__custom">Someone else — type their address…</option>
                </select>
              </div>
            )}
            {(eventContacts.contacts.length === 0 || contactChoice === "__custom") && (
              <input type="email" required value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                placeholder="organiser@example.com" data-testid="email-report-input"
                className="w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] mb-4" />
            )}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setEmailOpen(false)}
                className="h-12 px-5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-600">Cancel</button>
              <button type="submit" disabled={emailSending} data-testid="email-report-send"
                className="h-12 px-6 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-sm font-semibold disabled:opacity-60">
                {emailSending ? "Sending…" : "Send email"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
